import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Coco — floating AI concierge chat widget for public homepage.
 * Anonymous visitors can ask questions about services / booking. Bumps them
 * toward the skin quiz or the booking page.
 */
export default function ConciergeChat() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Hey gorgeous — I'm Coco, your 24K Glow concierge. Ask me about services, pricing, or booking, or take the 2-min skin quiz to get 50% off your first month. ✦",
        },
    ]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const endRef = useRef(null);

    useEffect(() => {
        if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [open, messages]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }]);
        setBusy(true);
        try {
            const { data } = await api.post("/ai/concierge", {
                session_id: sessionId,
                message: text,
                history: messages,
            });
            if (data.session_id) setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.reply || "…" }]);
        } catch (err) {
            setMessages((m) => [
                ...m,
                { role: "assistant", content: "I stepped out for a second — try again?" },
            ]);
        }
        setBusy(false);
    };

    return (
        <>
            {/* Floating trigger */}
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="concierge-open"
                    aria-label="Chat with Coco"
                    className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-gold via-rose to-gold text-ink shadow-2xl shadow-gold/30 flex items-center justify-center hover:scale-105 transition"
                >
                    <div className="relative">
                        <MessageCircle className="w-6 h-6" />
                        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-300 border-2 border-ink animate-pulse" />
                    </div>
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    data-testid="concierge-panel"
                    className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-96 h-[560px] max-h-[calc(100vh-3rem)] rounded-3xl border border-gold/30 bg-ink/95 backdrop-blur-xl shadow-2xl shadow-gold/20 flex flex-col overflow-hidden"
                >
                    {/* Header */}
                    <div className="p-4 border-b border-gold/20 flex items-center gap-3 bg-gradient-to-r from-gold/10 via-rose/10 to-gold/10">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-rose flex items-center justify-center shrink-0">
                            <Sparkles className="w-5 h-5 text-ink" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-serif text-base leading-tight">Coco</p>
                            <p className="text-[10px] tracking-widest uppercase text-gold/80">24K Glow Concierge</p>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="concierge-close"
                            aria-label="Close chat"
                            className="p-2 text-white/60 hover:text-white transition"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 luxe-scroll">
                        {messages.map((m, i) => (
                            <div
                                key={`msg-${i}`}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                                        m.role === "user"
                                            ? "bg-rose text-ink rounded-br-sm"
                                            : "bg-white/8 text-white rounded-bl-sm"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="px-4 py-2.5 bg-white/8 rounded-2xl rounded-bl-sm text-sm text-white/70 flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Coco is typing…
                                </div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    {/* Input */}
                    <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask me anything…"
                            data-testid="concierge-input"
                            disabled={busy}
                            className="flex-1 bg-surface border border-white/10 focus:border-gold rounded-full px-4 py-2.5 outline-none text-sm transition disabled:opacity-50"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            data-testid="concierge-send"
                            className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-gold to-rose text-ink flex items-center justify-center disabled:opacity-40 hover:scale-105 transition"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
